import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { appInstalled$, installPrompt$ } from './install-prompt';

/**
 * Offers "Add to Home screen" on phones and tablets.
 *
 * <p>Deliberately limited to small screens. On a desktop the browser already shows an
 * install button in the address bar, and a modal interrupting someone at a full-size
 * workstation is noise; on a tablet there is no such affordance and the home-screen
 * icon is the whole point.</p>
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  /** Remembers a "Not now" so the prompt is not shown on every single page load. */
  private static readonly SNOOZE_KEY = 'automatr.installPrompt.snoozedUntil';
  private static readonly SNOOZE_DAYS = 14;

  /** Matches the breakpoint the rest of the app treats as phone/tablet. */
  private static readonly SMALL_SCREEN = '(max-width: 991.98px)';

  /**
   * Wait before offering. Startup is the worst possible moment: the bootstrap spinner
   * is up, and an unauthenticated visit redirects to the identity server within a
   * second or two — a modal there is thrown away unseen, or worse, is the last thing
   * the user sees before the page vanishes. If we do redirect, the page unloads and
   * this timer dies with it.
   */
  private static readonly SETTLE_DELAY_MS = 4000;

  init(): void {
    if (!this.isSmallScreen() || this.isAlreadyInstalled() || this.isSnoozed()) {
      return;
    }

    setTimeout(() => this.offerWhenReady(), PwaInstallService.SETTLE_DELAY_MS);
  }

  private offerWhenReady(): void {
    // Re-check: they may have installed it, or rotated a tablet, in the meantime.
    if (this.isAlreadyInstalled()) {
      return;
    }

    // An install that happens any other way (browser menu, address bar) clears the
    // snooze so a reinstall later is offered normally.
    appInstalled$.pipe(filter(Boolean), take(1)).subscribe(() => this.clearSnooze());

    if (this.isIos()) {
      // Safari has no `beforeinstallprompt` and no programmatic install at all, so the
      // only thing that helps an iPad user is telling them where the button is.
      this.showIosInstructions();
      return;
    }

    // Chrome may fire the event before or after this runs; BehaviorSubject covers both.
    installPrompt$
      .pipe(filter(event => !!event), take(1))
      .subscribe(event => this.offerInstall(event));
  }

  private async offerInstall(deferredPrompt: any): Promise<void> {
    const result = await Swal.fire({
      title: 'Add AutomatR to your home screen',
      text: 'Open it like an app, without the browser bar.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Add',
      cancelButtonText: 'Not now',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (!result.isConfirmed) {
      this.snooze();
      return;
    }

    try {
      // The browser's own confirmation. It can only be opened once per captured
      // event, so the event is dropped either way afterwards.
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome !== 'accepted') {
        this.snooze();
      }
    } catch {
      // Already used, or the browser refused. Nothing to tell the user — they can
      // still install from the browser's own menu.
    } finally {
      installPrompt$.next(null);
    }
  }

  private async showIosInstructions(): Promise<void> {
    await Swal.fire({
      title: 'Add AutomatR to your home screen',
      html: 'Tap the <b>Share</b> button in Safari, then choose <b>Add to Home Screen</b>.',
      icon: 'info',
      confirmButtonText: 'Got it',
      confirmButtonColor: '#3085d6'
    });
    // Shown once; iOS cannot report whether they went through with it.
    this.snooze();
  }

  private isSmallScreen(): boolean {
    return typeof window !== 'undefined'
      && !!window.matchMedia
      && window.matchMedia(PwaInstallService.SMALL_SCREEN).matches;
  }

  /** True when the page is already running as an installed app. */
  private isAlreadyInstalled(): boolean {
    const standaloneDisplay = window.matchMedia?.('(display-mode: standalone)').matches;
    // iOS does not support display-mode and uses this non-standard flag instead.
    const iosStandalone = (window.navigator as any).standalone === true;
    return !!standaloneDisplay || iosStandalone;
  }

  private isIos(): boolean {
    const ua = window.navigator.userAgent;
    // iPadOS 13+ reports itself as a Mac; the touch points are what give it away.
    const iPadOs = /Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1;
    return /iPhone|iPad|iPod/.test(ua) || iPadOs;
  }

  // ── Snooze ────────────────────────────────────────────────────────
  // Storage can throw in private mode or with site data blocked; a failure here must
  // never stop the app, it just means the prompt may appear again next time.

  private isSnoozed(): boolean {
    try {
      const until = Number(localStorage.getItem(PwaInstallService.SNOOZE_KEY));
      return !!until && Date.now() < until;
    } catch {
      return false;
    }
  }

  private snooze(): void {
    try {
      const until = Date.now() + PwaInstallService.SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(PwaInstallService.SNOOZE_KEY, String(until));
    } catch {
      /* no storage — the prompt simply reappears next visit */
    }
  }

  private clearSnooze(): void {
    try {
      localStorage.removeItem(PwaInstallService.SNOOZE_KEY);
    } catch {
      /* nothing to clear */
    }
  }
}
